/* eslint-disable camelcase */
import axios from 'axios';
import type { PaymentMethod } from '@hotjar/webapp/billing/payment';
import { VaultedPaymentMethod } from '@hotjar/webapp/billing/payment';
import type { ApiBaseResponse } from '@hotjar/webapp/domains/common/api';
import { mapResponseErrorToMessage } from '@hotjar/webapp/domains/common/api';
import { daysFromNow } from '@hotjar/webapp/lib/time';

interface PaymentMethodDto {
  payment_method_nonce: string | undefined;
  payment_type: string | undefined;
  cardholder_name: string | undefined;
  country_code_alpha2: string | undefined;
  locality: string | undefined;
  postal_code: string | undefined;
}

const getPaymentMethodUrl = (accountId: number, version: 'v1' | 'v2' = 'v2') =>
  `/api/${version}/accounts/${accountId}/payment/details`;

function mapToPaymentMethodDto(paymentMethod: PaymentMethod): PaymentMethodDto {
  return {
    payment_method_nonce: paymentMethod.nonce,
    payment_type: paymentMethod.paymentType,
    cardholder_name: paymentMethod.cardholderName,
    country_code_alpha2: paymentMethod.countryCode,
    locality: paymentMethod.city,
    postal_code: paymentMethod.postCode,
  };
}

export interface PaymentMethodResponseDTO extends ApiBaseResponse {
  payment_type: IPaymentType;
  customer_card_info: {
    cardholder_name?: string;
    country_code_alpha2?: string;
    locality?: string;
    postal_code?: string;
  };
  nonce: string;
  bin: string;
  token: string;
}

function mapResponseToVaultedPaymentMethod(
  response: PaymentMethodResponseDTO,
): VaultedPaymentMethod {
  const { cardholder_name, country_code_alpha2, locality, postal_code } =
    response.customer_card_info ?? {};

  return new VaultedPaymentMethod(
    response.nonce,
    response.bin,
    response.payment_type,
    cardholder_name,
    country_code_alpha2,
    locality,
    postal_code,
    response.token,
  );
}

async function get(accountId: number): Promise<VaultedPaymentMethod> {
  const { data } = await axios.get<PaymentMethodResponseDTO>(getPaymentMethodUrl(accountId));

  if (!data.success) {
    throw new Error(mapResponseErrorToMessage(data.error));
  }

  return mapResponseToVaultedPaymentMethod(data);
}

async function vaultCard(
  accountId: number,
  paymentMethod: PaymentMethod,
): Promise<VaultedPaymentMethod> {
  const { data } = await axios.post<PaymentMethodResponseDTO>(
    getPaymentMethodUrl(accountId),
    mapToPaymentMethodDto(paymentMethod),
  );

  if (!data.success) {
    throw new Error(mapResponseErrorToMessage(data.error));
  }

  return mapResponseToVaultedPaymentMethod(data);
}

async function remove(accountId: number): Promise<ApiBaseResponse> {
  const { data } = await axios.post<PaymentMethodResponseDTO>(
    getPaymentMethodUrl(accountId, 'v1'),
    {
      action: 'remove_payment_method',
    },
  );

  if (!data.success) {
    throw new Error(mapResponseErrorToMessage(data.error));
  }

  return data;
}

export const paymentMethodAPI = { get, remove, vaultCard };

/**
 * Check if the subscription's credit card is about to expire.
 *
 * @param expiryDate Card's expiration date (epoch timestamp)
 */
export function isCardExpiringSoon(expiryDate: number | null | undefined): boolean {
  return expiryDate != null && daysFromNow(expiryDate) <= 30;
}
